/* eslint-disable no-param-reassign */
import FormSubmissionState from '@/enums/FormSubmissionState';
import OrderingPriority from '@/enums/OrderingPriority';
import VehicleType from '@/enums/VehicleType';
import { loggingService } from '@/services/LoggingService';
import IImageCollectionSearchModel from '@/models/image/interfaces/image-collection-search-model.d';
import IImageSearchModel from '@/models/image/interfaces/image-search-model.d';
import IEnquiry from '../models/interfaces/IEnquiry';
import Vehicle from '../models/vehicle/vehicle';
import ExtraData from '../models/ExtraData';

// eslint-disable-next-line import/no-cycle
import { store } from '../store';
import { EventTypeEnum, TrackingService } from './TrackingService';
import BeforeUnloadService from './BeforeUnloadService';

class EnquiryService {
  private static serviceInstance: EnquiryService;

  public static get instance() {
    // eslint-disable-next-line no-return-assign
    return this.serviceInstance || (this.serviceInstance = new this());
  }

  private apiDomain: string = '/umbraco/api/enquiry';

  private vehicleMatchServiceApiDomain: string = `${
    process.env.VUE_APP_API_DOMAIN
  }/umbraco/api/vehiclesearch`;

  public async submitEnquiry(
    enquiry: IEnquiry
  ): Promise<FormSubmissionState> {
    enquiry.refererUrl = window.location.href;

    return fetch(`${this.apiDomain}/enquiry`, {
      body: this.removeNullValuedProperties(enquiry),
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    })
      .then((response) => {
        if (response.ok) {
          loggingService.recordEvent(
            'eventTracking',
            'goal',
            'submit',
            'form'
          );

          loggingService.trackAIEvent(
            'FormSubmitted',
            { page: window.location },
            { vehicleId: enquiry.derivativeId }
          );

          return response.json();
        }

        throw new Error(response.statusText);
      })
      .then(async (json) => {
        if (json.enquiryId || json.enquiryGuid) {
          await TrackingService.instance.sendEvent(
            EventTypeEnum.FormComplete,
            store.getters['initialSettingsModule/correlationId'],
            {
               // enquiryId will be 0 as we move to the CAP gateway. 
               // This should be temporary and can be removed once the CAP gateway is fully in place.
              enquiryId: json.enquiryId > 0 ? json.enquiryId : null,
              enquiryGuid: json.enquiryGuid
            }
          );
        }

        // BEWARE! If you want to cast json to Enquiry
        // IE11 will lowercase property names for unknown reasons.
        if (json.thanksPageUrl !== '') {
          BeforeUnloadService.removeBeforeUnloadListener();
          window.location.href = json.thanksPageUrl;
        }

        store.dispatch('enquiryModule/submitFormSubmissionState', FormSubmissionState.SubmissionSuccess);

        return FormSubmissionState.SubmissionSuccess;
      })
      .catch((exception: Error) => {
        window.console.error('Failed to fetch Enquiry POST.', { exception });
        store.dispatch('enquiryModule/submitFormSubmissionState', FormSubmissionState.SubmissionFailed);

        return FormSubmissionState.SubmissionFailed;
      });
  }

  public async fetchRelatedVehicles(
    vehicleType: VehicleType,
    orderingPriority: OrderingPriority,
    rangeId: number
  ): Promise<Vehicle[]> {
    const requestUrl = `${this.vehicleMatchServiceApiDomain
    }/GetAutoMatchingRanges?vehicleType=${vehicleType}&orderingPriority=${orderingPriority}&rangeId=${rangeId}`;
    return fetch(requestUrl, {
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'GET'
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error(`${response.statusText}, calling ${requestUrl}`);
      })
      .then((result) => {
        if (result.length < 1) {
          throw new Error(
            `No vehicles returned, using fallback. Calling ${requestUrl}`
          );
        }
        return result;
      })
      .catch((error) => {
        window.console.error(
          'Failed to fetch matching vehicles from API - GET.',
          { error }
        );

        return [
          this.setVehicle(
            'BMW',
            'bmw',
            700,
            '1 Series',
            '1-series',
            3,
            'hatchback',
            74442
          ),

          this.setVehicle(
            'MINI',
            'mini',
            957,
            'Countryman',
            'countryman',
            670,
            'hatchback',
            65127
          ),

          this.setVehicle(
            'BMW',
            'bmw',
            34,
            '3 Series',
            '3-series',
            87,
            'saloon',
            74492
          ),

          this.setVehicle(
            'Renault',
            'renault',
            1017,
            'Captur',
            'captur',
            559,
            'hatchback',
            75400
          )
        ];
      });
  }

  // Deprecated - this will stop being used once we migrate to CAP Gateway
  public async updateEnquiryExtraData(extraData: ExtraData[], enquiryId: number) {
    const updateExtraData = {
      enquiryId,
      extraData
    };
    fetch(`${this.apiDomain}/UpdateEnquiryExtraData`, {
      body: this.removeNullValuedProperties(updateExtraData),
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    }).catch((error) => {
      window.console.error(
        'There was an issue updating enquiry extra data',
        error
      );
    });
  }

  public async updateEnquiryExtraDataByReferenceCode(extraData: ExtraData[], enquiryReferenceCode: string) {
    const updateExtraData = {
      enquiryReferenceCode,
      extraData
    };
    fetch(`${this.apiDomain}/UpdateEnquiryExtraDataByReference`, {
      body: this.removeNullValuedProperties(updateExtraData),
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    }).catch((error) => {
      window.console.error(
        'There was an issue updating enquiry extra data',
        error
      );
    });
  }

  private removeNullValuedProperties(object: any) {
    // eslint-disable-next-line consistent-return
    return JSON.stringify(object, (key, value) => {
      if (value !== null) {
        return value;
      }
    });
  }

  private setVehicle(
    manufacturerName: string,
    manufacturerNameSlug: string,
    rangeId: number,
    rangeName: string,
    rangeNameSlug: string,
    modelId: number,
    modelNameSlug: string,
    imageId: number
  ): Vehicle {
    const vehicle = new Vehicle();
    vehicle.manufacturerName = manufacturerName;
    vehicle.manufacturerNameSlug = manufacturerNameSlug;
    vehicle.rangeId = rangeId;
    vehicle.rangeName = rangeName;
    vehicle.rangeNameSlug = rangeNameSlug;
    vehicle.modelId = modelId;
    vehicle.modelNameSlug = modelNameSlug;
    vehicle.vehicleType = VehicleType.Car;
    vehicle.images = this.buildImageModel(imageId);
    return vehicle;
  }

  private buildImageModel(imageId: number): IImageCollectionSearchModel {
    const imageSearchModel: IImageSearchModel = {
      imageId
    };

    const images: IImageCollectionSearchModel = {
      basic: [imageSearchModel],
      lifestyle: []
    };

    return images;
  }
}
// export a singleton instance in the global namespace
// eslint-disable-next-line import/prefer-default-export
export const enquiryService = EnquiryService.instance;
